import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Users, FileText, Package, BookOpen, IndianRupee, Calendar, Gift, Plane, AlertCircle, Mail, MessageCircle, Loader2, CheckCircle, Globe, Map, Wallet, Receipt, Banknote, TrendingUp, Percent, ArrowRight, X, Download, Phone, Building2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import CurrencyRates from "../../components/CurrencyRates";
import TravelNews from "../../components/TravelNews";
import useAutoRefresh from "../../hooks/useAutoRefresh";
import generateQuotePdfDoc from "../../utils/quotePdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const AdminDashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveRates, setLiveRates] = useState(null);
  const [remindLoadingId, setRemindLoadingId] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState("all");
  const [dashStats, setDashStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [topAgents, setTopAgents] = useState([]);
  const [topAgentsLoading, setTopAgentsLoading] = useState(true);
  const [topAgentsPeriod, setTopAgentsPeriod] = useState("all");

  // Destination navigation — full page (no modal)
  const openDestPage = (destination) => {
    navigate(`/admin/destination/${encodeURIComponent(destination)}`);
  };

  useEffect(() => {
    fetchAnalytics();
    fetchLiveRates();
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      setStatsLoading(true);
      try {
        const statsRes = await axios.get(`${API}/admin/dashboard-stats?period=${statsPeriod}`, { headers: { Authorization: `Bearer ${token}` } });
        setDashStats(statsRes.data);
      } catch (error) {
        console.error("Error fetching admin dashboard stats:", error);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchAll();
  }, [statsPeriod, token]);

  useEffect(() => {
    const fetchTop = async () => {
      setTopAgentsLoading(true);
      try {
        const topRes = await axios.get(`${API}/admin/top-agents?period=${topAgentsPeriod}&limit=10`, { headers: { Authorization: `Bearer ${token}` } });
        setTopAgents(topRes.data || []);
      } catch (error) {
        console.error("Error fetching top agents:", error);
      } finally {
        setTopAgentsLoading(false);
      }
    };
    fetchTop();
  }, [topAgentsPeriod, token]);

  // Auto-refresh analytics (~1s) — new bookings/quotes/agents surface instantly
  useAutoRefresh(() => fetchAnalytics());

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveRates = async () => {
    try {
      const res = await axios.get(`${API}/currency-rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiveRates(res.data);
    } catch {}
  };

  const sendEmailReminder = async (booking) => {
    if (!booking?.agent_email) {
      toast.error("Agent email not available for this booking");
      return;
    }
    setRemindLoadingId(`email-${booking.booking_id}`);
    try {
      const res = await axios.post(
        `${API}/bookings/${booking.booking_id}/send-payment-reminder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Reminder emailed to ${res.data?.to || booking.agent_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder email");
    } finally {
      setRemindLoadingId(null);
    }
  };

  const sendWhatsAppReminder = (booking) => {
    const phone = (booking.agent_phone || "").replace(/[^0-9]/g, "");
    if (!phone) {
      toast.error("Agent phone number not available");
      return;
    }
    const bookingAmt = formatCurrency(booking.total_amount);
    const recdAmt = formatCurrency(booking.amount_paid);
    const balAmt = formatCurrency(booking.pending_amount);
    const msg =
      `Hi ${booking.agent_name || "there"}, this is a friendly payment reminder from Happy Holidays DMC for booking #${booking.booking_ref} (${booking.destination}, travel date ${formatDate(booking.travel_date)}).%0A%0A` +
      `Booking Amount: ${bookingAmt}%0A` +
      `Received Amount: ${recdAmt}%0A` +
      `Balance Amount: ${balAmt}%0A%0A` +
      `Kindly settle the pending balance at your earliest convenience. Thank you!`;
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
    // Log follow-up (best-effort; ignore errors)
    axios.post(
      `${API}/bookings/${booking.booking_id}/followups`,
      { type: "whatsapp", sent_to: booking.agent_phone || "", note: `WhatsApp reminder · Balance ${balAmt}` },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(() => {});
  };

  const [voucherLoadingId, setVoucherLoadingId] = useState(null);
  const [invoiceLoadingId, setInvoiceLoadingId] = useState(null);

  const downloadInvoice = async (booking) => {
    setInvoiceLoadingId(booking.booking_id);
    try {
      const res = await axios.get(`${API}/bookings/${booking.booking_id}/invoice/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${booking.invoice_number || booking.booking_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download invoice");
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  const downloadVoucher = async (booking) => {
    setVoucherLoadingId(booking.booking_id);
    try {
      // Use the SAME rich-itinerary PDF that admin gets in Booking Details.
      // Admin context: branding stays Happy Holidays DMC (advisor = logged-in admin profile).
      const [bookingRes, meRes] = await Promise.all([
        axios.get(`${API}/bookings/${booking.booking_id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
      ]);
      const fullBooking = bookingRes.data;
      const me = meRes.data || {};
      const advisor = {
        name: me.name || '',
        email: me.email || '',
        phone: me.phone || '',
        company_name: me.company_name || 'Happy Holidays DMC',
        designation: me.designation || '',
        address: me.address || '',
        city: me.city || '',
        state: me.state || '',
        website: me.website || '',
        logo_url: me.logo_url || '',
        profile_picture_url: me.profile_picture_url || '',
      };
      const quoteId = fullBooking.quote_id;
      if (!quoteId) { toast.error("No linked quotation found"); return; }
      const quoteRes = await axios.get(`${API}/quotes/${quoteId}`, { headers: { Authorization: `Bearer ${token}` } });
      let q = quoteRes.data;
      // Reflect any booking-level hotel/flight edits in the PDF.
      if (Array.isArray(fullBooking.hotels_override) && fullBooking.hotels_override.length > 0) {
        q = {
          ...q,
          hotels: fullBooking.hotels_override,
          accommodation_options: (q.accommodation_options && q.accommodation_options.length > 0)
            ? [{ ...q.accommodation_options[0], hotels: fullBooking.hotels_override }, ...q.accommodation_options.slice(1)]
            : [{ hotels: fullBooking.hotels_override }],
        };
      }
      if (Array.isArray(fullBooking.flights_override) && fullBooking.flights_override.length > 0) {
        q = { ...q, flights: fullBooking.flights_override };
      }
      const bno = fullBooking.booking_number || (fullBooking.id || '').slice(0, 8).toUpperCase();
      const doc = generateQuotePdfDoc(q, advisor, {
        bookingConfirmations: fullBooking.confirmations || [],
        bookingMode: true,
        bookingNumber: bno,
      });
      if (!doc) { toast.error("Could not generate voucher"); return; }
      doc.save(`Booking_${bno}.pdf`);
      toast.success("Voucher downloaded!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download voucher");
    } finally {
      setVoucherLoadingId(null);
    }
  };

  const overview = analytics?.overview || {};

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6" data-testid="admin-dashboard">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900 mb-1">Admin Dashboard</h1>
            </div>
            <p className="text-gray-500">Business overview and upcoming activities</p>
          </div>
          {liveRates && (
            <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2.5 shadow-sm" data-testid="inline-currency-rates">
              <span className="text-[10px] text-white bg-emerald-500 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wide">Live</span>
              {[
                { key: "USD_INR", code: "USD", flag: "$" },
                { key: "SGD_INR", code: "SGD", flag: "S$" },
                { key: "THB_INR", code: "THB", flag: "\u0E3F" },
              ].map(({ key, code, flag }, idx) => (
                <div key={key} className="flex items-center gap-1.5">
                  {idx > 0 && <span className="text-gray-200">|</span>}
                  <span className="text-xs text-gray-500">{flag} {code}</span>
                  <span className="text-sm font-bold text-gray-900 tabular-nums">₹{liveRates[key]?.toFixed(2) || '-'}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bookings Performance Snapshot */}
        <Card className="bg-white border-gray-200 shadow-sm" data-testid="admin-bookings-snapshot">
          <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              Bookings Performance
            </CardTitle>
            <select
              value={statsPeriod}
              onChange={(e) => setStatsPeriod(e.target.value)}
              className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="admin-stats-period"
            >
              <option value="all">All Time</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_fy">This Financial Year</option>
              <option value="last_fy">Last Financial Year</option>
            </select>
          </CardHeader>
          <CardContent>
            {(() => {
              const s = dashStats || {};
              const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
              const inr = (n) => `₹${fmt(n)}`;
              const a = s.pax_adults || 0;
              const c = s.pax_children || 0;
              const paxStr = c > 0 ? `${a}A + ${c}C` : `${a}A`;
              const tiles = [
                { label: "Total Bookings", value: fmt(s.total_bookings), icon: Package, fg: "text-blue-700", bg: "bg-blue-50", testid: "admin-stat-total-bookings" },
                { label: "Completed", value: fmt(s.completed_bookings), icon: CheckCircle, fg: "text-emerald-700", bg: "bg-emerald-50", testid: "admin-stat-completed" },
                { label: "Total Pax", value: paxStr, icon: Users, fg: "text-violet-700", bg: "bg-violet-50", testid: "admin-stat-pax" },
                { label: "Domestic", value: fmt(s.domestic), icon: Map, fg: "text-amber-700", bg: "bg-amber-50", testid: "admin-stat-domestic" },
                { label: "International", value: fmt(s.international), icon: Globe, fg: "text-cyan-700", bg: "bg-cyan-50", testid: "admin-stat-international" },
                { label: "Cancelled", value: fmt(s.cancelled_bookings), icon: X, fg: "text-rose-700", bg: "bg-rose-50", testid: "admin-stat-cancelled" },
                { label: "Booking Amount", value: inr(s.total_booking_amount), icon: Receipt, fg: "text-indigo-700", bg: "bg-indigo-50", testid: "admin-stat-booking-amount" },
                { label: "Received Amount", value: inr(s.total_paid_amount), icon: Banknote, fg: "text-teal-700", bg: "bg-teal-50", testid: "admin-stat-paid-amount" },
                { label: "Balance Amount", value: inr(s.total_balance_amount), icon: Wallet, fg: "text-rose-700", bg: "bg-rose-50", testid: "admin-stat-balance-amount" },
                { label: "GST Amount (billed)", value: inr(s.total_gst_amount), icon: Receipt, fg: "text-amber-700", bg: "bg-amber-50", testid: "admin-stat-gst-amount" },
                { label: "Profit Amount", value: inr(s.total_profit_amount), icon: TrendingUp, fg: "text-emerald-700", bg: "bg-emerald-50", testid: "admin-stat-profit-amount" },
                { label: "Profit Margin", value: `${Number(s.profit_margin || 0).toFixed(2)}%`, icon: Percent, fg: "text-fuchsia-700", bg: "bg-fuchsia-50", testid: "admin-stat-profit-margin" },
              ];
              if (statsLoading && !dashStats) {
                return <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-6 gap-3">{Array.from({length:11}).map((_,i)=>(<div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />))}</div>;
              }
              return (
                <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-6 gap-3">
                  {tiles.map((t, i) => (
                    <div key={i} className="rounded-xl border border-gray-100 bg-white px-4 py-3 hover:shadow-sm transition-all" data-testid={t.testid}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${t.bg} flex items-center justify-center shrink-0`}>
                          <t.icon className={`w-5 h-5 ${t.fg}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] text-gray-500 uppercase tracking-wide truncate">{t.label}</p>
                          <p className="font-bold text-gray-900 tabular-nums truncate text-base" title={t.value}>{t.value}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Top 10 Agents Leaderboard */}
        <Card className="bg-white border-gray-200 shadow-sm" data-testid="admin-top-agents">
          <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-600" />
              Top 10 Agents
            </CardTitle>
            <select
              value={topAgentsPeriod}
              onChange={(e) => setTopAgentsPeriod(e.target.value)}
              className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="top-agents-period"
            >
              <option value="all">All Time</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_fy">This Financial Year</option>
              <option value="last_fy">Last Financial Year</option>
            </select>
          </CardHeader>
          <CardContent>
            {topAgentsLoading && topAgents.length === 0 ? (
              <div className="space-y-2">{Array.from({length:5}).map((_,i)=>(<div key={i} className="h-14 rounded-md bg-gray-100 animate-pulse" />))}</div>
            ) : topAgents.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No agent bookings in this period.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">Agent</th>
                      <th className="px-3 py-2 text-right">Bookings</th>
                      <th className="px-3 py-2 text-right">Completed</th>
                      <th className="px-3 py-2 text-right">Pax</th>
                      <th className="px-3 py-2 text-right">Domestic</th>
                      <th className="px-3 py-2 text-right">Intl.</th>
                      <th className="px-3 py-2 text-right">Booking Amt</th>
                      <th className="px-3 py-2 text-right">Received</th>
                      <th className="px-3 py-2 text-right">Balance</th>
                      <th className="px-3 py-2 text-right" title="GST is counted once an invoice is generated for the booking">GST (billed)</th>
                      <th className="px-3 py-2 text-right">Profit</th>
                      <th className="px-3 py-2 text-right">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topAgents.map((a, i) => {
                      const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
                      const paxStr = (a.pax_children || 0) > 0 ? `${a.pax_adults || 0}A + ${a.pax_children}C` : `${a.pax_adults || 0}A`;
                      const isDirect = a.agent_id === "DIRECT";
                      const displayCompany = isDirect ? "Direct Client" : a.company_name;
                      const displayName = isDirect ? "Walk-in / Admin-created bookings" : a.name;
                      const handleRowClick = () => {
                        if (isDirect) {
                          navigate(`/admin/bookings?agent_id=DIRECT`);
                        } else {
                          navigate(`/admin/agents/${encodeURIComponent(a.agent_id)}`);
                        }
                      };
                      return (
                        <tr
                          key={a.agent_id}
                          className="border-b border-gray-100 hover:bg-emerald-50/40 cursor-pointer transition-colors"
                          onClick={handleRowClick}
                          data-testid={`top-agent-${i}`}
                          title={isDirect ? "View all Direct Client bookings" : `View all bookings of ${a.company_name || a.name || 'this agent'}`}
                        >
                          <td className="px-3 py-3 font-bold text-gray-400 tabular-nums">{i + 1}</td>
                          <td className="px-3 py-3">
                            <p className={`font-semibold text-sm leading-tight ${isDirect ? 'text-violet-700' : 'text-gray-900'}`}>{displayCompany}</p>
                            <p className="text-gray-500 text-xs leading-tight">{displayName}</p>
                          </td>
                          <td className="px-3 py-3 text-right tabular-nums text-gray-800">{a.total_bookings}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-emerald-700">{a.completed_bookings}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-gray-700">{paxStr}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-amber-700">{a.domestic}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-cyan-700">{a.international}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-indigo-700 font-semibold">{inr(a.total_booking_amount)}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-teal-700">{inr(a.total_paid_amount)}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-rose-600">{inr(a.total_balance_amount)}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-amber-700">{inr(a.total_gst_amount)}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-emerald-700 font-semibold">{inr(a.total_profit_amount)}</td>
                          <td className="px-3 py-3 text-right tabular-nums text-fuchsia-700 font-semibold">{Number(a.profit_margin || 0).toFixed(2)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top 5 Domestic & International Destinations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="admin-top-destinations">
          {[
            { title: "Top 5 Domestic Destinations", list: dashStats?.top_domestic_destinations || [], chip: "bg-amber-50 text-amber-700", icon: Map, accent: "text-amber-600" },
            { title: "Top 5 International Destinations", list: dashStats?.top_international_destinations || [], chip: "bg-cyan-50 text-cyan-700", icon: Globe, accent: "text-cyan-600" },
          ].map((card, idx) => (
            <Card key={idx} className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <card.icon className={`w-5 h-5 ${card.accent}`} />
                  {card.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {card.list.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No bookings in this period.</p>
                ) : (
                  <div className="overflow-x-auto" data-testid={idx === 0 ? "top-domestic-list" : "top-international-list"}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                          <th className="py-2 pr-2 font-semibold">#</th>
                          <th className="py-2 pr-2 font-semibold">Destination</th>
                          <th className="py-2 pr-2 font-semibold text-center">Bookings</th>
                          <th className="py-2 pr-2 font-semibold text-center">Pax</th>
                          <th className="py-2 pr-2 font-semibold text-right">Booking Amt</th>
                          <th className="py-2 pr-2 font-semibold text-right">Profit Amt</th>
                          <th className="py-2 font-semibold text-right">Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {card.list.map((d, i) => {
                          const ba = Number(d.booking_amount || 0);
                          const pa = Number(d.profit_amount || 0);
                          const margin = Number(d.profit_margin || 0);
                          const adults = Number(d.pax_adults || 0);
                          const children = Number(d.pax_children || 0);
                          const fmtMoney = (n) => `${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
                          return (
                            <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                              <td className="py-2 pr-2">
                                <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs font-bold flex items-center justify-center tabular-nums">{i + 1}</span>
                              </td>
                              <td className="py-2 pr-2 font-medium text-gray-800">
                                <button
                                  onClick={() => openDestPage(d.destination)}
                                  className="text-emerald-700 hover:text-emerald-900 hover:underline font-semibold text-left transition-colors"
                                  data-testid={`dest-link-${d.destination}`}
                                >
                                  {d.destination}
                                </button>
                              </td>
                              <td className="py-2 pr-2 text-center">
                                <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${card.chip}`}>{d.count}</span>
                              </td>
                              <td className="py-2 pr-2 text-center text-gray-700 tabular-nums whitespace-nowrap">{adults}A{children > 0 ? ` + ${children}C` : ''}</td>
                              <td className="py-2 pr-2 text-right text-gray-800 tabular-nums">₹{fmtMoney(ba)}</td>
                              <td className="py-2 pr-2 text-right text-emerald-700 tabular-nums font-semibold">₹{fmtMoney(pa)}</td>
                              <td className={`py-2 text-right tabular-nums font-semibold ${margin >= 25 ? 'text-emerald-700' : margin >= 10 ? 'text-amber-600' : 'text-rose-600'}`}>{margin}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Travel News */}
        <TravelNews />

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {/* Inquiries */}
              <Card onClick={() => navigate('/admin/quotes')} className="bg-white border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer" data-testid="dash-card-inquiries">
                <CardContent className="p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-gray-500 text-xs uppercase font-semibold tracking-wider">Inquiries</p>
                      <div className="mt-2">
                        <span className="text-3xl font-bold text-gray-800">{overview.total_inquiries || 0}</span>
                      </div>
                      <div className="mt-1">
                        <span className="text-sm font-medium text-amber-600">{overview.pending_inquiries || 0} pending</span>
                      </div>
                    </div>
                    <div className="p-2.5 bg-blue-50 rounded-xl">
                      <FileText className="w-5 h-5 text-blue-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quotes */}
              <Card onClick={() => navigate('/admin/quotes')} className="bg-white border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer" data-testid="dash-card-quotes">
                <CardContent className="p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-gray-500 text-xs uppercase font-semibold tracking-wider">Quotes</p>
                      <div className="mt-2">
                        <span className="text-3xl font-bold text-gray-800">{overview.total_quotes || 0}</span>
                      </div>
                      <div className="mt-1">
                        <span className="text-sm font-medium text-pink-600">{overview.pending_quotes || 0} pending</span>
                      </div>
                    </div>
                    <div className="p-2.5 bg-pink-50 rounded-xl">
                      <BookOpen className="w-5 h-5 text-pink-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bookings */}
              <Card onClick={() => navigate('/admin/bookings')} className="bg-white border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer" data-testid="dash-card-bookings">
                <CardContent className="p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-gray-500 text-xs uppercase font-semibold tracking-wider">Bookings</p>
                      <div className="mt-2">
                        <span className="text-3xl font-bold text-gray-800">{overview.confirmed_bookings || 0}</span>
                      </div>
                      <div className="mt-1">
                        <span className="text-sm font-medium text-gray-500">Confirmed</span>
                      </div>
                    </div>
                    <div className="p-2.5 bg-emerald-50 rounded-xl">
                      <Package className="w-5 h-5 text-emerald-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Booking Value */}
              <Card onClick={() => navigate('/admin/payments')} className="bg-white border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer" data-testid="dash-card-total-value">
                <CardContent className="p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-gray-500 text-xs uppercase font-semibold tracking-wider">Total Booking Amount</p>
                      <div className="mt-2">
                        <p className="text-2xl font-bold text-gray-800">{formatCurrency(overview.total_booking_amount)}</p>
                      </div>
                    </div>
                    <div className="p-2.5 bg-amber-50 rounded-xl">
                      <IndianRupee className="w-5 h-5 text-amber-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Received Amount */}
              <Card onClick={() => navigate('/admin/payments')} className="bg-gradient-to-br from-emerald-500 to-emerald-600 border-0 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all cursor-pointer" data-testid="dash-card-received">
                <CardContent className="p-5">
                  <div>
                    <p className="text-emerald-100 text-xs uppercase font-semibold tracking-wider">Total Received Amount</p>
                    <div className="mt-2">
                      <p className="text-2xl font-bold text-white">{formatCurrency(overview.total_received)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pending Amount */}
              <Card onClick={() => navigate('/admin/payments')} className="bg-gradient-to-br from-red-500 to-red-600 border-0 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all cursor-pointer" data-testid="dash-card-pending">
                <CardContent className="p-5">
                  <div>
                    <p className="text-red-100 text-xs uppercase font-semibold tracking-wider">Total Balance Amount</p>
                    <div className="mt-2">
                      <p className="text-2xl font-bold text-white">{formatCurrency(overview.total_pending)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Upcoming Bookings - Full Width */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-2 border-b border-gray-100">
                <CardTitle className="text-gray-900 flex items-center gap-2 text-lg">
                  <Plane className="w-5 h-5 text-blue-500" />
                  Upcoming Travel Bookings
                  <span className="text-sm font-normal text-gray-500">(45 days)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {analytics?.upcoming_bookings?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="upcoming-bookings-table">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Travel Date</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking ID</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Agent</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Guest Name</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Destination</th>
                          <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking Amt</th>
                          <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Received Amt</th>
                          <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Balance Amt</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {[...analytics.upcoming_bookings].sort((a, b) => new Date(a.travel_date || '9999-12-31') - new Date(b.travel_date || '9999-12-31')).map((booking, index) => {
                          const adults = Number(booking.pax_adults || 0);
                          const cwb = Number(booking.cwb || 0);
                          const cnb = Number(booking.cnb || 0);
                          const paxParts = [`${adults} Adult${adults !== 1 ? 's' : ''}`];
                          if (cwb > 0) paxParts.push(`${cwb} CWB`);
                          if (cnb > 0) paxParts.push(`${cnb} CNB`);
                          const paxStr = paxParts.join(' + ');
                          return (
                          <tr key={booking.booking_id || index} className="hover:bg-gray-50 transition-colors" data-testid={`upcoming-booking-row-${index}`}>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span className="font-medium text-gray-800">{formatDate(booking.travel_date)}</span>
                              </div>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <span className="font-mono text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded text-xs">
                                #{booking.booking_ref}
                              </span>
                            </td>
                            <td className="px-3 py-3">
                              <div className="text-gray-800 font-medium text-sm">{booking.company_name || booking.agent_name || '—'}</div>
                              {booking.agent_name && booking.company_name && booking.agent_name !== booking.company_name && (
                                <div className="text-xs text-gray-500">{booking.agent_name}</div>
                              )}
                            </td>
                            <td className="px-3 py-3">
                              <div className="text-gray-800 font-medium">{booking.guest_name}</div>
                              <div className="text-xs text-gray-500">{paxStr}</div>
                            </td>
                            <td className="px-3 py-3">
                              <div className="text-gray-800 font-medium">{booking.destination}</div>
                              <div className="text-xs text-gray-500">{booking.nights > 0 ? `${booking.nights} Nights` : '—'}</div>
                            </td>
                            <td className="px-3 py-3 text-right whitespace-nowrap">
                              <span className="text-gray-800 font-semibold">{formatCurrency(booking.total_amount)}</span>
                            </td>
                            <td className="px-3 py-3 text-right whitespace-nowrap">
                              <span className="text-emerald-700 font-semibold">{formatCurrency(booking.amount_paid)}</span>
                            </td>
                            <td className="px-3 py-3 text-right whitespace-nowrap">
                              <span className={`font-semibold px-2 py-1 rounded ${
                                booking.pending_amount > 0
                                  ? "text-red-600 bg-red-50"
                                  : "text-emerald-600 bg-emerald-50"
                              }`}>
                                {formatCurrency(booking.pending_amount)}
                              </span>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <div className="flex items-center justify-center gap-2">
                                {Number(booking.pending_amount || 0) === 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => downloadVoucher(booking)}
                                    disabled={voucherLoadingId === booking.booking_id}
                                    title="Download Voucher PDF"
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium"
                                    data-testid={`voucher-download-btn-${index}`}
                                  >
                                    {voucherLoadingId === booking.booking_id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Download className="w-4 h-4" />
                                    )}
                                    Voucher
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => sendEmailReminder(booking)}
                                      disabled={remindLoadingId === `email-${booking.booking_id}` || !booking.agent_email}
                                      title={booking.agent_email ? `Send payment reminder to ${booking.agent_email}` : "Agent email unavailable"}
                                      className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                      data-testid={`reminder-email-btn-${index}`}
                                    >
                                      {remindLoadingId === `email-${booking.booking_id}` ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Mail className="w-4 h-4" />
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => sendWhatsAppReminder(booking)}
                                      disabled={!booking.agent_phone}
                                      title={booking.agent_phone ? `WhatsApp ${booking.agent_phone}` : "Agent phone unavailable"}
                                      className="p-2 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                      data-testid={`reminder-whatsapp-btn-${index}`}
                                    >
                                      <MessageCircle className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Plane className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No upcoming bookings in the next 45 days</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Completed Tours */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="admin-completed-tours">
              <CardHeader className="pb-2 border-b border-gray-100 flex flex-row items-center justify-between">
                <CardTitle className="text-gray-900 flex items-center gap-2 text-lg">
                  <CheckCircle className="w-5 h-5 text-slate-500" />
                  Completed Tours <span className="text-xs font-normal text-gray-500 ml-1">(Last 10)</span>
                </CardTitle>
                <Link to="/admin/bookings">
                  <Button variant="ghost" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 gap-1 text-sm">
                    View All <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent className="p-0">
                {analytics?.completed_bookings?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50/80">
                        <tr>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tour End Date</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking ID</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Agent</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Guest Name</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Destination</th>
                          <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking Amt</th>
                          <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Received Amt</th>
                          <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Balance Amt</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {analytics.completed_bookings.map(booking => {
                          const adults = Number(booking.pax_adults || 0);
                          const cwb = Number(booking.cwb || 0);
                          const cnb = Number(booking.cnb || 0);
                          const paxParts = [`${adults} Adult${adults !== 1 ? 's' : ''}`];
                          if (cwb > 0) paxParts.push(`${cwb} CWB`);
                          if (cnb > 0) paxParts.push(`${cnb} CNB`);
                          const paxStr = paxParts.join(' + ');
                          return (
                            <tr key={booking.booking_id} className="hover:bg-gray-50/50" data-testid={`admin-completed-row-${booking.booking_id}`}>
                              <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDate(booking.return_date || booking.travel_date)}</td>
                              <td className="px-3 py-3">
                                <Link to={`/admin/bookings/${booking.booking_id}`}
                                  className="font-mono text-slate-600 font-medium bg-slate-50 px-2 py-1 rounded text-xs hover:bg-slate-100 transition-colors">
                                  #{booking.booking_ref}
                                </Link>
                              </td>
                              <td className="px-3 py-3">
                                <div className="text-sm font-medium text-gray-800">{booking.company_name || booking.agent_name || '—'}</div>
                                {booking.agent_name && booking.company_name && booking.agent_name !== booking.company_name && (
                                  <div className="text-xs text-gray-500">{booking.agent_name}</div>
                                )}
                              </td>
                              <td className="px-3 py-3">
                                <p className="text-sm font-medium text-gray-800">{booking.guest_name}</p>
                                <p className="text-xs text-gray-500">{paxStr}</p>
                              </td>
                              <td className="px-3 py-3 text-sm text-gray-700">
                                <p className="font-medium text-gray-800">{booking.destination}</p>
                                <p className="text-xs text-gray-500">{booking.nights > 0 ? `${booking.nights} Nights` : '—'}</p>
                              </td>
                              <td className="px-3 py-3 text-right text-sm font-semibold text-gray-800">₹{Number(booking.total_amount || 0).toLocaleString()}</td>
                              <td className="px-3 py-3 text-right text-sm font-semibold text-emerald-600">₹{Number(booking.amount_paid || 0).toLocaleString()}</td>
                              <td className="px-3 py-3 text-right text-sm font-semibold">
                                <span className={Number(booking.pending_amount || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}>
                                  ₹{Number(booking.pending_amount || 0).toLocaleString()}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-center whitespace-nowrap">
                                {booking.invoice_id ? (
                                  <button
                                    type="button"
                                    onClick={() => downloadInvoice(booking)}
                                    disabled={invoiceLoadingId === booking.booking_id}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 text-xs font-medium transition-colors"
                                    title={`Download invoice ${booking.invoice_number}`}
                                    data-testid={`download-invoice-${booking.booking_id}`}
                                  >
                                    {invoiceLoadingId === booking.booking_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Receipt className="w-3.5 h-3.5" />}
                                    Invoice
                                  </button>
                                ) : (
                                  <span className="text-[11px] text-gray-400 italic" title="No invoice generated yet">Not generated</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No completed tours yet.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Agent Birthdays */}
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-2 border-b border-gray-100">
                <CardTitle className="text-gray-900 flex items-center gap-2 text-lg">
                  <Gift className="w-5 h-5 text-purple-500" />
                  Agent Birthdays This Month
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.birthdays?.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {analytics.birthdays.map((agent, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl border border-purple-100">
                        <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold text-lg">
                          {agent.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-800 font-medium text-sm truncate">{agent.name}</p>
                          <p className="text-gray-500 text-xs truncate">{agent.company_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-purple-600 text-sm font-bold">
                            {new Date(agent.birthday).getDate()}
                          </p>
                          <p className="text-purple-500 text-xs">
                            {new Date(agent.birthday).toLocaleString('default', { month: 'short' })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Gift className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No birthdays this month</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

    </DashboardLayout>
  );
};

export default AdminDashboard;
